import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useLeagueScreen } from './useLeagueScreen';
import {
  IDLE_MS,
  STALE_MS,
  STANDINGS_PAGE_MS,
  STANDINGS_PAGE_SIZE,
  formatClock,
  formatGameTime,
  formatShortDate,
  gamesOn,
  leaderOf,
  pageCount,
  pageRows,
  setLine,
  standingsColumns,
} from './screenUtils';
import './leagueScreen.css';

/**
 * Venue screen: a full-screen board for a TV beside the court on a league
 * night — live scores, standings, the games still to come and the last
 * results. Route /screen/league/:id, signed in or not (a private league only
 * for people who may see it). No app navigation on this route.
 */

// Games the main panel lists under "Next up" when nothing is live
const NEXT_UP = 4;

const subtitleOf = (league) => [league.sport_type, league.season, league.venue_name].filter(Boolean).join(' · ');

const Brand = () => (
  <div className="vs-brand" aria-label="Sportifide">
    <img src="/logo192.png" alt="" className="vs-brand__logo" />
    <span className="vs-brand__name">Sportifide</span>
  </div>
);

const useNow = (everyMs = 1000) => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), everyMs);
    return () => clearInterval(t);
  }, [everyMs]);
  return now;
};

/** Hide the cursor and the controls after IDLE_MS without mouse movement. */
const useIdle = (ms = IDLE_MS) => {
  const [idle, setIdle] = useState(false);
  useEffect(() => {
    let timer = setTimeout(() => setIdle(true), ms);
    const wake = () => {
      setIdle(false);
      clearTimeout(timer);
      timer = setTimeout(() => setIdle(true), ms);
    };
    window.addEventListener('mousemove', wake);
    window.addEventListener('touchstart', wake);
    window.addEventListener('keydown', wake);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('mousemove', wake);
      window.removeEventListener('touchstart', wake);
      window.removeEventListener('keydown', wake);
    };
  }, [ms]);
  return idle;
};

const FullscreenButton = ({ targetRef }) => {
  const [isFull, setIsFull] = useState(false);
  useEffect(() => {
    const onChange = () => setIsFull(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);
  if (typeof document === 'undefined' || !document.fullscreenEnabled) return null;
  const toggle = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen?.();
    } else {
      targetRef.current?.requestFullscreen?.().catch(() => {});
    }
  };
  return (
    <button type="button" className="vs-control" onClick={toggle}>
      {isFull ? 'Exit full screen' : 'Full screen'}
    </button>
  );
};

/** A friendly full-screen message (private, not found, error, loading). */
const ScreenMessage = ({ title, children, tone = 'neutral' }) => (
  <div className="venue-screen venue-screen--message">
    <div className="vs-message">
      <Brand />
      <h1 className={`vs-message__title vs-message__title--${tone}`}>{title}</h1>
      {children && <p className="vs-message__text">{children}</p>}
    </div>
  </div>
);

const TeamLine = ({ name, sets, points, leading, showSets }) => (
  <div className={`vs-live__team${leading ? ' is-leading' : ''}`}>
    <span className="vs-live__name">{name}</span>
    {showSets && <span className="vs-live__sets" title="Sets won">{sets}</span>}
    <span className="vs-live__points">{points}</span>
  </div>
);

const LiveCard = ({ match, setSport }) => {
  const { score } = match;
  const leader = leaderOf(score);
  const showSets = setSport && score?.sets_won;
  const period = score?.period_label || (setSport && score?.current_set ? `Set ${score.current_set}` : null);
  const sets = setLine(score);
  return (
    <article className="vs-live" data-testid="live-match">
      <header className="vs-live__head">
        <span className="vs-court">{match.court || 'Court TBD'}</span>
        <span className="vs-live__badge"><span className="vs-dot" aria-hidden="true" />LIVE{period ? ` · ${period}` : ''}</span>
      </header>
      <TeamLine name={match.team_1_name} sets={score?.sets_won?.p1 ?? 0} points={score?.points?.p1 ?? 0} leading={leader === 'p1'} showSets={showSets} />
      <TeamLine name={match.team_2_name} sets={score?.sets_won?.p2 ?? 0} points={score?.points?.p2 ?? 0} leading={leader === 'p2'} showSets={showSets} />
      {sets && <footer className="vs-live__setline">Sets: {sets}</footer>}
    </article>
  );
};

const NextUpCard = ({ game, today }) => (
  <article className="vs-next" data-testid="next-up-game">
    <div className="vs-next__when">
      <span className="vs-next__time">{formatGameTime(game.game_time)}</span>
      {game.game_date !== today && <span className="vs-next__date">{formatShortDate(game.game_date)}</span>}
    </div>
    <span className="vs-court">{game.court || 'Court TBD'}</span>
    <div className="vs-next__teams">
      <span>{game.team_1_name}</span>
      <span className="vs-vs">vs</span>
      <span>{game.team_2_name}</span>
    </div>
  </article>
);

const MainPanel = ({ data }) => {
  const setSport = Boolean(data.rules?.uses_sets);
  if (data.live.length > 0) {
    return (
      <section className="vs-panel vs-main" aria-labelledby="vs-live-title">
        <h2 id="vs-live-title" className="vs-panel__title"><span className="vs-dot" aria-hidden="true" />Live now</h2>
        <div className={`vs-live-grid vs-live-grid--${Math.min(data.live.length, 6)}`}>
          {data.live.map((m) => <LiveCard key={m.id} match={m} setSport={setSport} />)}
        </div>
      </section>
    );
  }
  const next = data.upcoming.slice(0, NEXT_UP);
  return (
    <section className="vs-panel vs-main" aria-labelledby="vs-next-title">
      <h2 id="vs-next-title" className="vs-panel__title">Next up</h2>
      {next.length > 0 ? (
        <div className="vs-next-list">
          {next.map((g) => <NextUpCard key={g.id} game={g} today={data.date} />)}
        </div>
      ) : (
        <p className="vs-empty">No games scheduled. See you next league night!</p>
      )}
    </section>
  );
};

const Standings = ({ rows, rules }) => {
  const [page, setPage] = useState(0);
  const pages = pageCount(rows);
  useEffect(() => {
    if (pages <= 1) {
      setPage(0);
      return undefined;
    }
    const t = setInterval(() => setPage((p) => (p + 1) % pages), STANDINGS_PAGE_MS);
    return () => clearInterval(t);
  }, [pages]);
  const columns = standingsColumns(rules);
  const shown = pageRows(rows, page);
  const first = (page % pages) * STANDINGS_PAGE_SIZE + 1;
  return (
    <section className="vs-panel vs-standings" aria-labelledby="vs-standings-title">
      <h2 id="vs-standings-title" className="vs-panel__title">
        Standings
        {pages > 1 && (
          <span className="vs-panel__meta" data-testid="standings-page">
            {first}–{first + shown.length - 1} of {rows.length}
          </span>
        )}
      </h2>
      {rows.length === 0 ? (
        <p className="vs-empty">Standings appear after the first results.</p>
      ) : (
        <table className="vs-table">
          <thead>
            <tr>
              <th className="vs-table__rank" scope="col">#</th>
              <th className="vs-table__team" scope="col">Team</th>
              {columns.map((c) => <th key={c.key} scope="col" title={c.title}>{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {shown.map((t) => (
              <tr key={`${t.position}-${t.team_name}`} className={t.position <= 3 ? `is-top is-top-${t.position}` : ''}>
                <td className="vs-table__rank">{t.position}</td>
                <td className="vs-table__team">
                  {t.team_name}
                  {t.withdrawn && <span className="vs-withdrawn">withdrawn</span>}
                </td>
                {columns.map((c) => <td key={c.key} className={c.strong ? 'vs-table__strong' : ''}>{c.value(t)}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
};

const ResultLine = ({ game }) => {
  const won1 = game.team_1_score > game.team_2_score;
  const won2 = game.team_2_score > game.team_1_score;
  return (
    <li className="vs-strip__item" data-testid="final-result">
      <span className={won1 ? 'is-winner' : ''}>{game.team_1_name}</span>
      <span className="vs-strip__score">{game.team_1_score ?? '–'}–{game.team_2_score ?? '–'}</span>
      <span className={won2 ? 'is-winner' : ''}>{game.team_2_name}</span>
    </li>
  );
};

const BottomStrip = ({ data }) => {
  // When nothing is live the main panel shows the first NEXT_UP games: not again here
  const inMain = new Set(data.live.length > 0 ? [] : data.upcoming.slice(0, NEXT_UP).map((g) => g.id));
  const later = gamesOn(data.upcoming, data.date).filter((g) => !inMain.has(g.id)).slice(0, 6);
  return (
    <section className="vs-strip" aria-label="Later today and final results">
      <div className="vs-strip__block">
        <h3 className="vs-strip__title">Later today</h3>
        {later.length > 0 ? (
          <ul className="vs-strip__list">
            {later.map((g) => (
              <li key={g.id} className="vs-strip__item" data-testid="later-today">
                <span className="vs-strip__time">{formatGameTime(g.game_time)}</span>
                <span className="vs-strip__court">{g.court || 'Court TBD'}</span>
                <span>{g.team_1_name} <span className="vs-vs">vs</span> {g.team_2_name}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="vs-strip__empty">No more games today</p>
        )}
      </div>
      <div className="vs-strip__block">
        <h3 className="vs-strip__title">Final results</h3>
        {data.results.length > 0 ? (
          <ul className="vs-strip__list">
            {data.results.map((g) => <ResultLine key={g.id} game={g} />)}
          </ul>
        ) : (
          <p className="vs-strip__empty">No results yet</p>
        )}
      </div>
    </section>
  );
};

const LeagueScreen = () => {
  const { id } = useParams();
  const { status, data, lastUpdated } = useLeagueScreen(id);
  const now = useNow();
  const idle = useIdle();
  const rootRef = useRef(null);

  const title = data?.league?.name;
  useEffect(() => {
    const previous = document.title;
    if (title) document.title = `${title} · Live board`;
    return () => { document.title = previous; };
  }, [title]);

  const stale = Boolean(lastUpdated) && now.getTime() - lastUpdated > STALE_MS;

  if (status === 'loading') {
    return <ScreenMessage title="Loading the league board…" />;
  }
  if (status === 'private') {
    return (
      <ScreenMessage title="This league isn't public" tone="warn">
        Only the league's members can see its live board. Sign in on this device with an account in the league, or ask the organizer to make the league public.
      </ScreenMessage>
    );
  }
  if (status === 'not_found') {
    return (
      <ScreenMessage title="League not found" tone="warn">
        Check the link: this league may have been removed.
      </ScreenMessage>
    );
  }
  if (status === 'error' || !data) {
    return (
      <ScreenMessage title="Can't reach Sportifide" tone="warn">
        Retrying automatically every few seconds.
      </ScreenMessage>
    );
  }

  return (
    <div ref={rootRef} className={`venue-screen${idle ? ' is-idle' : ''}`} data-testid="venue-screen">
      <header className="vs-header">
        <div className="vs-header__title">
          <h1 className="vs-league-name">{data.league.name}</h1>
          <p className="vs-league-sub">{subtitleOf(data.league)}</p>
        </div>
        <div className="vs-header__side">
          <div className="vs-controls">
            <FullscreenButton targetRef={rootRef} />
          </div>
          {stale && <span className="vs-stale" role="status">Reconnecting…</span>}
          <time className="vs-clock" dateTime={now.toISOString()}>{formatClock(now)}</time>
          <Brand />
        </div>
      </header>

      <div className="vs-body">
        <MainPanel data={data} />
        <Standings rows={data.standings} rules={data.rules} />
      </div>

      <BottomStrip data={data} />
    </div>
  );
};

export default LeagueScreen;
